import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { addVendorPaymentValidationSchema } from '../../../validations/SocietyPortal/VendorValidation';
import { getAllVendors, createVendorPayment, getVendorUnpaidBills, getVendorBillingSettings, getVendorGlCodeDropdown } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import * as routes from '../../../Routing/routes';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { PAYMENT_TYPES } from '../../../utils/helpers/paymentType';
import { notify } from '../../../utils/notification';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'neft', label: 'NEFT' },
];

const TDS_SECTIONS = ['194C', '194J', '194I', '194H', '194A', '194Q'];

// 01-01 is CASH & BANK BALANCES — the accounts a society can actually pay out of.
const CASH_BANK_GROUP = '01-01';
// The API forces cash payments onto this account, so the form mirrors it.
const CASH_IN_HAND_CODE = '01-01-01-01';

const AddVendorPayment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { allVendorsData, allVendorsLoading, createVendorPaymentLoading, vendorUnpaidBillsData, vendorUnpaidBillsLoading, vendorBillingSettingsData, vendorGlCodeDropdownData, vendorGlCodeDropdownLoading } = useSelector(state => state.VendorReducers);
  const [billPayments, setBillPayments] = useState([]);
  const [documentFile, setDocumentFile] = useState(null);
  // 'Draft' saves the entry without touching the books; 'Post' writes the journal entry.
  const [generateStatus, setGenerateStatus] = useState('Post');

  useEffect(() => {
    dispatch(getAllVendors({ society_id: selectedSocietyStorer.getSocietyId() }));
    // TDS defaults live on the society's billing settings, not on the vendor.
    dispatch(getVendorBillingSettings({ societyId: selectedSocietyStorer.getSocietyId() }));
    // Cash & bank accounts to pay out of — this is the journal's debit side.
    dispatch(getVendorGlCodeDropdown({
      societyId: selectedSocietyStorer.getSocietyId(),
      type: 'account',
      groupGlCode: CASH_BANK_GROUP,
    }));
  }, [dispatch]);

  const cashBankAccounts = useMemo(() => {
    if (!Array.isArray(vendorGlCodeDropdownData)) return [];
    return vendorGlCodeDropdownData.map(a => ({ code: a.ac_code, name: a.name }));
  }, [vendorGlCodeDropdownData]);

  const vendorsList = useMemo(() => {
    if (!Array.isArray(allVendorsData)) return [];
    return allVendorsData.map(vendor => ({
      id: vendor.vendor_id,
      name: vendor.business_name,
      glCode: vendor.vendor_gl_code || '',
      tdsApplicable: vendor.tds_applicable || 'N',
    }));
  }, [allVendorsData]);

  // The society decides whether TDS applies and at what default rate; the vendor's own
  // tds_applicable narrows it to vendors it should be deducted for. `vendors` has no
  // tds_rate/tds_section columns, which is why the old silent calculation always gave 0.
  const tdsSettings = useMemo(() => ({
    enabled: Number(vendorBillingSettingsData?.tds_applicable) === 1,
    defaultRate: parseFloat(vendorBillingSettingsData?.default_tds_rate) || 0,
    defaultSection: vendorBillingSettingsData?.tds_section || '',
  }), [vendorBillingSettingsData]);
  const unpaidBills = useMemo(() => {
    const bills = Array.isArray(vendorUnpaidBillsData)
      ? vendorUnpaidBillsData
      : vendorUnpaidBillsData?.bills || [];
    return bills.map(bill => ({
      id: bill.bill_id || bill.id,
      billDate: bill.bill_date,
      billDueDate: bill.due_date,
      billNumber: bill.bill_number,
      billAmount: parseFloat(bill.total_amount || bill.netamount || bill.total || 0),
      amountDue: parseFloat(bill.balance_amount || bill.total_amount || 0),
    }));
  }, [vendorUnpaidBillsData]);

  // Bills that exist but are still drafts: they cannot be paid until posted, and
  // an empty "Bills to Pay" list otherwise looks like the vendor owes nothing.
  const draftBillCount = Number(vendorUnpaidBillsData?.draftBills) || 0;

  const handleVendorChange = useCallback((vendorId, setFieldValue) => {
    setFieldValue('vendorId', vendorId);
    setBillPayments([]);

    // Seed TDS from the society settings, narrowed by the vendor's own flag. Both stay
    // editable — the operator can override per payment.
    const vendor = vendorsList.find(v => String(v.id) === String(vendorId));
    const applies = tdsSettings.enabled && vendor?.tdsApplicable === 'Y';
    setFieldValue('tdsApplicable', applies);
    setFieldValue('tdsRate', applies ? tdsSettings.defaultRate : '');
    setFieldValue('tdsSection', applies ? tdsSettings.defaultSection : '');

    if (vendorId) {
      dispatch(getVendorUnpaidBills({
        vendor_id: vendorId,
        societyId: selectedSocietyStorer.getSocietyId(),
      }));
    }
  }, [dispatch, vendorsList, tdsSettings]);

  const handleBillPaymentChange = useCallback((billId, value) => {
    const amount = parseFloat(value) || 0;
    setBillPayments(prev => {
      const existing = prev.find(bp => bp.billId === billId);
      if (existing) {
        if (amount === 0) return prev.filter(bp => bp.billId !== billId);
        return prev.map(bp => bp.billId === billId ? { ...bp, payment: amount } : bp);
      }
      if (amount > 0) return [...prev, { billId, payment: amount }];
      return prev;
    });
  }, []);

  const totalBillPayment = useMemo(() => {
    return billPayments.reduce((sum, bp) => sum + bp.payment, 0);
  }, [billPayments]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount);
  };

  const handleSubmit = (values) => {
    const selectedVendor = vendorsList.find(v => String(v.id) === String(values.vendorId));
    const paymentMade = parseFloat(values.amount) || 0;
    const tdsApplicable = values.tdsApplicable ? 'Y' : 'N';
    const tdsRate = parseFloat(values.tdsRate) || 0;
    const tdsAmount = tdsApplicable === 'Y' ? +((paymentMade * tdsRate) / 100).toFixed(2) : 0;
    const amountAfterTds = +(paymentMade - tdsAmount).toFixed(2);

    // Build bills array from unpaidBills with their payment amounts
    const billsWithPayments = unpaidBills
      .map(bill => {
        const bp = billPayments.find(bp => bp.billId === bill.id);
        return {
          id: bill.id,
          date: bill.billDate,
          dueDate: bill.billDueDate,
          billNo: bill.billNumber,
          billAmount: bill.billAmount,
          amountDue: bill.amountDue,
          payment: bp?.payment || 0,
        };
      })
      .filter(bill => bill.payment > 0);

    // A bill payment with nothing allocated is an advance under the wrong name;
    // the API rejects it, so say so here rather than after a round trip.
    if (values.paymentType === 'bill_payment' && billsWithPayments.length === 0) {
      notify('error', unpaidBills.length === 0 && draftBillCount > 0
        ? `This vendor's ${draftBillCount} bill(s) are still drafts — post them on the Vendor Bills screen, or switch Payment Type to Advance Payment.`
        : 'Enter the amount to pay against at least one bill, or switch Payment Type to Advance Payment.');
      return;
    }

    dispatch(createVendorPayment({
      societyId: selectedSocietyStorer.getSocietyId(),
      vendorId: values.vendorId,
      vendorName: selectedVendor?.name || '',
      vendorGlCode: selectedVendor?.glCode || '',
      paymentType: values.paymentType,
      paymentDate: values.paymentDate,
      paymentMade: paymentMade,
      paymentMode: values.paymentMode,
      paidThrough: values.paymentMode === 'cash' ? CASH_IN_HAND_CODE : values.paidThrough,
      utrNo: values.referenceNo || '',
      paymentDoc: documentFile || null,
      notes: values.notes || '',
      amountUsedForPayments: totalBillPayment,
      dueAmount: paymentMade - totalBillPayment,
      tdsApplicable: tdsApplicable,
      tdsRate: tdsRate,
      tdsSection: values.tdsSection || '',
      tdsAmount: tdsAmount,
      amountAfterTds: amountAfterTds,
      generateStatus,
      bills: billsWithPayments,
      onSuccess: () => {
        navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS);
      },
    }));
  };

  return (
    <>
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
        <button
          onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS)}
          className="tw-p-1.5 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500"
        >
          <Icon icon="mdi:arrow-left" className="tw-text-lg" />
        </button>
        <div>
          <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Record Vendor Payment</h1>
          <p className="tw-text-xs tw-text-gray-500">Record a new payment for a vendor</p>
        </div>
      </div>

      <Formik
        initialValues={{
          vendorId: '',
          paymentType: '',
          paymentDate: '',
          amount: '',
          paymentMode: '',
          referenceNo: '',
          notes: '',
          paidThrough: '',
          tdsApplicable: false,
          tdsRate: '',
          tdsSection: '',
        }}
        enableReinitialize={false}
        validationSchema={addVendorPaymentValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <div className="tw-grid tw-grid-cols-12 tw-gap-4">
              {/* Left: Payment Form */}
              <div className="tw-col-span-8">
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                  <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">Payment Details</h2>

                  <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3">
                    {/* Vendor Name */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Vendor Name *</label>
                      <Field
                        as="select"
                        name="vendorId"
                        onChange={(e) => handleVendorChange(e.target.value, setFieldValue)}
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                        disabled={allVendorsLoading}
                      >
                        <option value="">Select vendor</option>
                        {vendorsList.map(v => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="vendorId" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>

                    {/* Payment Type */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Type *</label>
                      <Field
                        as="select"
                        name="paymentType"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                      >
                        <option value="">Select type</option>
                        {PAYMENT_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="paymentType" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>
                  </div>

                  <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3">
                    {/* Payment Date */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Date *</label>
                      <Field
                        type="date"
                        name="paymentDate"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                      />
                      <ErrorMessage name="paymentDate" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>

                    {/* Payment Made (Amount) */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Made *</label>
                      <Field
                        type="number"
                        name="amount"
                        placeholder="0.00"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                      />
                      <ErrorMessage name="amount" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>
                  </div>

                  <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3">
                    {/* Payment Mode */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Mode *</label>
                      <Field
                        as="select"
                        name="paymentMode"
                        onChange={(e) => {
                          const mode = e.target.value;
                          setFieldValue('paymentMode', mode);
                          // Keep the disabled cash selection visible rather than sending a
                          // value the form never showed.
                          if (mode === 'cash') setFieldValue('paidThrough', CASH_IN_HAND_CODE);
                          else if (values.paidThrough === CASH_IN_HAND_CODE) setFieldValue('paidThrough', '');
                        }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                      >
                        <option value="">Select mode</option>
                        {PAYMENT_MODES.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="paymentMode" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>

                    {/* Paid Through — the account the money leaves. Without it the payment
                        has no debit side and cannot post to the ledger. */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                        Paid Through {values.paymentMode !== 'cash' && '*'}
                      </label>
                      <Field
                        as="select"
                        name="paidThrough"
                        disabled={vendorGlCodeDropdownLoading || values.paymentMode === 'cash'}
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                      >
                        <option value="">
                          {vendorGlCodeDropdownLoading ? 'Loading accounts...' : 'Select bank / cash account'}
                        </option>
                        {cashBankAccounts.map(a => (
                          <option key={a.code} value={a.code}>{a.name} ({a.code})</option>
                        ))}
                      </Field>
                      {values.paymentMode === 'cash' ? (
                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">Cash payments always post to Cash in Hand</p>
                      ) : (
                        <ErrorMessage name="paidThrough" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                      )}
                      {!vendorGlCodeDropdownLoading && cashBankAccounts.length === 0 && (
                        <p className="tw-text-[10px] tw-text-amber-600 tw-mt-0.5">No cash or bank accounts found. Add them under Accounts before posting payments.</p>
                      )}
                    </div>

                    {/* UTR/Cheque No */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">UTR/Cheque No.</label>
                      <Field
                        type="text"
                        name="referenceNo"
                        placeholder="Enter UTR or Cheque number"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                      />
                    </div>
                  </div>

                  {/* TDS — shown for every payment type, advances included. It was
                      previously computed invisibly at submit time from vendor columns that
                      don't exist, so it always came out as zero. */}
                  {values.vendorId && tdsSettings.enabled && (
                    <div className="tw-mb-3 tw-border tw-border-orange-100 tw-bg-orange-50/50 tw-rounded-lg tw-p-3">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-1.5">
                          <Icon icon="mdi:file-percent" className="tw-text-orange-500" />
                          TDS Deduction
                        </h3>
                        <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                          <input
                            type="checkbox"
                            checked={values.tdsApplicable}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setFieldValue('tdsApplicable', on);
                              if (on) {
                                if (!values.tdsRate) setFieldValue('tdsRate', tdsSettings.defaultRate);
                                if (!values.tdsSection) setFieldValue('tdsSection', tdsSettings.defaultSection);
                              }
                            }}
                            className="tw-rounded tw-border-gray-300"
                          />
                          Deduct TDS on this payment
                        </label>
                      </div>
                      {values.tdsApplicable && (
                        <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                          <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TDS Rate (%)</label>
                            <Field
                              type="number"
                              name="tdsRate"
                              min="0"
                              max="100"
                              step="0.01"
                              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TDS Section</label>
                            <Field
                              as="select"
                              name="tdsSection"
                              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                            >
                              <option value="">Select section</option>
                              {TDS_SECTIONS.map(sec => (
                                <option key={sec} value={sec}>{sec}</option>
                              ))}
                            </Field>
                          </div>
                          <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TDS Amount</label>
                            <div className="tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-gray-50 tw-text-gray-700">
                              {formatCurrency(((parseFloat(values.amount) || 0) * (parseFloat(values.tdsRate) || 0)) / 100)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Upload Document */}
                  <div className="tw-mb-3">
                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Upload Document</label>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="file"
                        onChange={(e) => setDocumentFile(e.target.files[0] || null)}
                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      />
                      {documentFile && (
                        <button
                          type="button"
                          onClick={() => setDocumentFile(null)}
                          className="tw-p-1 tw-text-red-400 hover:tw-text-red-600"
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Bills to Pay Table — only shown for 'bill_payment'. Advance
                      payments hide this section since they aren't tied to a bill.
                      A bill payment must allocate at least one bill: an
                      unallocated one is just an advance, and filing it as a bill
                      payment left the bills looking unpaid. */}
                  {values.vendorId && values.paymentType === 'bill_payment' && (
                    <div className="tw-mt-4">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-1.5">
                          <Icon icon="mdi:file-document-outline" className="tw-text-blue-500" />
                          Bills to Pay <span className="tw-text-rose-500 tw-font-normal tw-text-xs">*</span>
                        </h3>
                        <span className="tw-text-[11px] tw-text-gray-500">
                          Enter the amount to pay against each bill.
                        </span>
                      </div>
                      {vendorUnpaidBillsLoading ? (
                        <div className="tw-text-center tw-py-4">
                          <Icon icon="mdi:loading" className="tw-animate-spin tw-text-xl tw-text-blue-500" />
                          <p className="tw-text-xs tw-text-gray-500 tw-mt-1">Loading bills...</p>
                        </div>
                      ) : unpaidBills.length === 0 ? (
                        <div className="tw-text-center tw-py-4 tw-bg-gray-50 tw-rounded-lg">
                          <Icon icon="mdi:file-document-remove-outline" className="tw-text-2xl tw-text-gray-300" />
                          {draftBillCount > 0 ? (
                            <p className="tw-text-xs tw-text-gray-600 tw-mt-1">
                              This vendor has {draftBillCount} draft bill{draftBillCount > 1 ? 's' : ''}. Post {draftBillCount > 1 ? 'them' : 'it'} on the{' '}
                              <button
                                type="button"
                                onClick={() => navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS)}
                                className="tw-text-blue-600 hover:tw-underline tw-font-medium"
                              >
                                Vendor Bills
                              </button>{' '}
                              screen to pay {draftBillCount > 1 ? 'them' : 'it'} here, or switch Payment Type to Advance Payment.
                            </p>
                          ) : (
                            <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                              No unpaid bills for this vendor. To pay them before a bill is raised, switch Payment Type to Advance Payment.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                          <table className="tw-w-full tw-text-sm">
                            <thead>
                              <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Bill Date</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Bill No#</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Bill Amount</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Amount Due</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-w-[140px]">Payment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {unpaidBills.map((bill) => {
                                const currentPayment = billPayments.find(bp => bp.billId === bill.id)?.payment || '';
                                return (
                                  <tr key={bill.id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-2">
                                      <p className="tw-text-gray-600">{bill.billDate ? new Date(bill.billDate).toLocaleDateString('en-IN') : '-'}</p>
                                      {bill.billDueDate && <p className="tw-text-[10px] tw-text-gray-400">Due: {new Date(bill.billDueDate).toLocaleDateString('en-IN')}</p>}
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                      <span className="tw-font-medium tw-text-blue-600">{bill.billNumber}</span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-800">
                                      {formatCurrency(bill.billAmount)}
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-red-600">
                                      {formatCurrency(bill.amountDue)}
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right">
                                      <input
                                        type="number"
                                        value={currentPayment}
                                        onChange={(e) => handleBillPaymentChange(bill.id, e.target.value)}
                                        max={bill.amountDue}
                                        min={0}
                                        step="0.01"
                                        placeholder="0.00"
                                        className="tw-w-[120px] tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded tw-text-sm tw-text-right focus:tw-outline-none focus:tw-border-blue-500"
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  <div className="tw-mt-3">
                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                      Notes <span className="tw-text-gray-400">(Internal use. Not visible to vendor)</span>
                    </label>
                    <Field
                      as="textarea"
                      name="notes"
                      rows="2"
                      placeholder="Add internal notes..."
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Right: Payment Summary */}
              <div className="tw-col-span-4">
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-sticky tw-top-4">
                  <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4">Payment Summary</h2>

                  <div className="tw-space-y-3">
                    <div className="tw-flex tw-justify-between tw-text-sm">
                      <span className="tw-text-gray-500">Amount Paid:</span>
                      <span className="tw-font-semibold tw-text-gray-800">
                        {formatCurrency(parseFloat(values.amount) || 0)}
                      </span>
                    </div>

                    {values.tdsApplicable && (
                      <>
                        <div className="tw-flex tw-justify-between tw-text-sm">
                          <span className="tw-text-gray-500">TDS ({parseFloat(values.tdsRate) || 0}%{values.tdsSection ? ` · ${values.tdsSection}` : ''}):</span>
                          <span className="tw-font-semibold tw-text-orange-600">
                            -{formatCurrency(((parseFloat(values.amount) || 0) * (parseFloat(values.tdsRate) || 0)) / 100)}
                          </span>
                        </div>
                        <div className="tw-flex tw-justify-between tw-text-sm">
                          <span className="tw-text-gray-500">Net Payable:</span>
                          <span className="tw-font-semibold tw-text-gray-800">
                            {formatCurrency((parseFloat(values.amount) || 0) - ((parseFloat(values.amount) || 0) * (parseFloat(values.tdsRate) || 0)) / 100)}
                          </span>
                        </div>
                      </>
                    )}

                    {values.paymentType === 'bill_payment' && (
                      <div className="tw-flex tw-justify-between tw-text-sm">
                        <span className="tw-text-gray-500">Amount used for Payments:</span>
                        <span className="tw-font-semibold tw-text-gray-800">
                          {formatCurrency(totalBillPayment)}
                        </span>
                      </div>
                    )}

                    {values.paymentType === 'bill_payment' && (
                      <>
                        <hr className="tw-border-gray-100" />
                        <div className="tw-flex tw-justify-between tw-text-sm">
                          <span className="tw-text-gray-500">Amount in Excess:</span>
                          <span className={`tw-font-semibold ${(parseFloat(values.amount) || 0) - totalBillPayment >= 0 ? 'tw-text-emerald-600' : 'tw-text-red-600'}`}>
                            {formatCurrency((parseFloat(values.amount) || 0) - totalBillPayment)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="tw-mt-6 tw-space-y-2">
                    <button
                      type="submit"
                      onClick={() => setGenerateStatus('Post')}
                      disabled={createVendorPaymentLoading}
                      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                      {createVendorPaymentLoading && generateStatus === 'Post' ? (
                        <>
                          <Icon icon="mdi:loading" className="tw-animate-spin" />
                          Recording...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:check" />
                          Record &amp; Post
                        </>
                      )}
                    </button>
                    <button
                      type="submit"
                      onClick={() => setGenerateStatus('Draft')}
                      disabled={createVendorPaymentLoading}
                      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-border tw-border-amber-300 tw-text-amber-700 tw-bg-amber-50 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-amber-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                      {createVendorPaymentLoading && generateStatus === 'Draft' ? (
                        <>
                          <Icon icon="mdi:loading" className="tw-animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:file-edit-outline" />
                          Save as Draft
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS)}
                      className="tw-w-full tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddVendorPayment;
